import { type RequiredFileModel, type Stored, type UUID } from "@overmap-ai/common"
import { BaseOfflineFileModelApiService } from "@overmap-ai/core"
import { type NodeViewProps, NodeViewWrapper } from "@tiptap/react"

import { useFetchFileFromFileModel } from "../../../hooks/utils"
import { AppSDK } from "../../../sdk"
import type { RootState } from "../../../store"
import { genericMemo } from "../../../utils/optimization"
import { FileNodeComponent } from "./FileNodeComponent"
import { ImageFileNodeComponent } from "./ImageFileNodeComponent"

interface BaseAttachmentNodeComponentProps<TAttachment extends UUID<RequiredFileModel>> extends NodeViewProps {
	attachment: Stored<TAttachment>
	service: BaseOfflineFileModelApiService<string, TAttachment, RootState, AppSDK>
}

export const BaseAttachmentNodeComponent = genericMemo(function <TAttachment extends UUID<RequiredFileModel>>(
	props: BaseAttachmentNodeComponentProps<TAttachment>,
) {
	const { attachment, service } = props

	const isImageAttachment = attachment.file_type && attachment.file_type.startsWith("image/")

	const [fetchedFile, failed] = useFetchFileFromFileModel(attachment, service)

	return (
		<NodeViewWrapper className="my-4 flex justify-center">
			{isImageAttachment ? (
				<ImageFileNodeComponent
					file={fetchedFile}
					error={failed ? attachment.file_name : undefined}
					{...props}
				/>
			) : (
				<FileNodeComponent file={fetchedFile} error={failed ? attachment.file_name : undefined} {...props} />
			)}
		</NodeViewWrapper>
	)
})
